import React, { useEffect } from "react";
import lodash from "lodash";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { toBase64 } from "@/main/services/utilsFile";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { getTranslateByKey } from "@/main/services/utils";
import * as S from "../styles";
import { SaveSupervisorRequest } from "@/main/models/request/supervisorRequest";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getStatusRegister, useStatusRegisterReducer } from "@/main/store/statusRegister";
import ContactInfo, { formattedContactSave, IContactInfoState } from "@/main/components/shared/contactInfo";
import AddressInfo, { IAddressInfoState } from "@/main/components/shared/addressInfo";
import {
	ManufacturerRequest,
	DistributionRegionFilterRequest,
	ReasonFilterRequest,
	AddressSaveRequest,
} from "@/main/models/request";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { getDistributionRegion } from "@/main/store/distributionRegion";
import { getReason } from "@/main/store/reason";
import { getSupplierType } from "@/main/store/supplierType";
import { getManufacturers } from "@/main/store/vehicle/manufacturer";
import { ISupervisorRegisterForm, useSupervisorRegisterContext } from "./supervisorRegisterContext";
import { KOGAreaButtons } from "@/main/components/shared/styles";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { CustomError } from "@/main/services/class/customError";
import { toast } from "react-toastify";
import { Guid } from "guid-typescript";
import { supervisorApi } from "@/main/api";
import { RoutesConstants } from "@/main/routes/routesConstants";
import InsurerCompany from "@/main/components/shared/insurerCompanySupervisor";
import { InsurerBySupervisorResponse } from "@/main/models/response/supervisorResponse";
import Box from "@/main/components/shared/box";
import { useUserReducer } from "@/main/store/user/userHooks";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";

interface ITabSupervisorRegister {
	dataContacts: IContactInfoState[];
	initialDataContacts: IContactInfoState[];
	dataAddress: IAddressInfoState[];
	initialDataAddress: IAddressInfoState[];
	dataInsurers: InsurerBySupervisorResponse[];
	guidSupervisorParams: string | null;
	setDataAddress: React.Dispatch<React.SetStateAction<IAddressInfoState[]>>;
	setDataContacts: React.Dispatch<React.SetStateAction<IContactInfoState[]>>;
	loadInsurersBySupervisor: (guid: string, guidInsurer: string) => void;
	loadInfoSupervisor: (guid: string, guidInsurer: string) => void;
}

export default function TabSupervisorRegister({
	dataAddress,
	initialDataAddress,
	dataContacts,
	initialDataContacts,
	dataInsurers,
	guidSupervisorParams,
	setDataAddress,
	setDataContacts,
	loadInsurersBySupervisor,
	loadInfoSupervisor,
}: ITabSupervisorRegister) {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { countriesUser } = useCountryReducer();
	const { statusRegister } = useStatusRegisterReducer();
	const [, setSearchParams] = useSearchParams();

	const { isEdit, setIsEdit } = useSupervisorRegisterContext();
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	const methods = useFormContext<ISupervisorRegisterForm>();
	const watcherCountry = methods.watch("country");

	const userReducer = useUserReducer();
	const guidInsurer =
		userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer ? userReducer.currentUser?.guidCompany : null;

	const {
		control,
		setValue,
		formState: { errors },
	} = useFormContext();

	useEffect(() => {
		dispatch(getCountriesUser());
		dispatch(getStatusRegister());
	}, []);

	useEffect(() => {
		if (countriesUser.data?.length === 1) {
			const countryValue = countriesUser.data[0];
			setValue("country", countryValue);
			methods.setValue("country", countryValue);
		}
	}, [countriesUser.data?.length]);

	useEffect(() => {
		if (watcherCountry !== null) {
			dispatch(getSupplierType());

			const requestMake: ManufacturerRequest = {};
			dispatch(getManufacturers(requestMake));

			const requestRegion: DistributionRegionFilterRequest = {
				guidCountry: String(watcherCountry?.key),
			};
			dispatch(getDistributionRegion(requestRegion));

			const requestReason: ReasonFilterRequest = {
				guidCountry: String(watcherCountry?.key),
				idReasonType: ReasonTypeEnum.Blacklist,
			};
			dispatch(getReason(requestReason));
		}
	}, [watcherCountry]);

	useEffect(() => {
		if (guidSupervisorParams) {
			setIsEdit(true);
			loadInsurersBySupervisor(guidSupervisorParams, guidInsurer ?? "");
		}
	}, [guidSupervisorParams]);

	const isFormChanged = methods.formState.isDirty;
	const isContactsChanged = !lodash.isEqual(initialDataContacts, dataContacts);
	const isAddressChanged = !lodash.isEqual(initialDataAddress, dataAddress);
	const hasChanges = isFormChanged || isContactsChanged || isAddressChanged;

	const isDisabledCountry = () => {
		return countriesUser.data?.length === 1;
	};

	function handleBack() {
		navigate(RoutesConstants.registrationSearchSupervisor);
	}

	function validateForm() {
		const messages = [];

		const supervisorName = methods.getValues("name");

		if (!supervisorName) messages.push(getTranslateByKey("information.nameRequired", false));

		if (dataContacts.length === 0) messages.push(getTranslateByKey("information.mustHaveAtLeastOneContact", true));

		if (dataContacts.length > 0) {
			const primaryContact = dataContacts.find((contact) => contact.isPrimaryContact);

			if (!primaryContact) messages.push(getTranslateByKey("information.mustHaveAtLeastOnePrimaryContact", true));
		}

		if (messages.length > 0) {
			messages.map((message) => toast.error(message));
			throw new CustomError(messages.join("/n"), "MISSING_FIELDS");
		}
	}

	const formatDataAddress = (addresses: IAddressInfoState[], isEditAddress = false) => {
		return addresses.map<AddressSaveRequest>((address) => ({
			...(isEditAddress && {
				guid: Guid.isGuid(address.id) ? address.id : Guid.EMPTY,
			}),
			idAddressType: address.addressType.key as number,
			street: address.address,
			complement: address.complement,
			number: address.number,
			municipality: {
				guid: address.municipality.key.toString(),
			},
		}));
	};

	async function handleSubmitEditSupervisor(data: ISupervisorRegisterForm) {
		try {
			validateForm();

			const isFormChanged = methods.formState.isDirty;
			const isContactsChanged = !lodash.isEqual(initialDataContacts, dataContacts);
			const isAddressChanged = !lodash.isEqual(initialDataAddress, dataAddress);

			if (!isFormChanged && !isContactsChanged && !isAddressChanged) {
				return;
			}

			let filesRequest = undefined;
			let guidReason = null;
			let reasonDescription = null;

			const resultConfirm = await getConfirmationReason(
				getTranslateByKey("information.confirmChangeTitle"),
				ReasonTypeEnum.ChangeCompany,
			);

			if (!resultConfirm.confirmed) return;

			if (resultConfirm.files && resultConfirm.files.length > 0) {
				filesRequest = await Promise.all(
					resultConfirm.files.map(async (item) => {
						const base64DataUrl = await toBase64(item);
						return {
							fileName: item.name,
							contentType: item.type,
							fileBase64: base64DataUrl as string,
						};
					}),
				);
			}
			guidReason = resultConfirm.reason ? String(resultConfirm.reason.key) : null;
			reasonDescription = resultConfirm.observation || null;

			const formattedContacts = formattedContactSave(dataContacts, true);
			const formattedAddress = formatDataAddress(dataAddress, true);

			const request: SaveSupervisorRequest = {
				guid: String(guidSupervisorParams),
				name: String(data.name),
				registrationNumber: String(data.rfc),
				idStatusRegister: Number(data.statusRegister?.key),
				guidCountry: String(data.country?.key),
				addresses: formattedAddress,
				contacts: formattedContacts,
				insurerIds: [],
				supplierCompanyRelations: [],
				guidInsurerLog: guidInsurer ?? "",
				changeJustification: {
					guidReason: guidReason,
					reasonDescription: reasonDescription,
					filesUpload: filesRequest,
				},
			};

			supervisorApi.save(request).then((response) => {
				const { result } = response.data;
				toast.success(getTranslateByKey("information.supervisorEditSuccessfully"));
				setIsEdit(true);
				setSearchParams({ supervisorGuid: result.guid }, { replace: true });
				loadInfoSupervisor(result.guid, guidInsurer ?? "");
			});
		} catch {}
	}

	function handleSubmitRegisterSupervisor(data: ISupervisorRegisterForm) {
		try {
			validateForm();
			const formattedContacts = formattedContactSave(dataContacts);
			const formattedAddress = formatDataAddress(dataAddress);

			const request: SaveSupervisorRequest = {
				name: String(data.name),
				registrationNumber: String(data.rfc),
				idStatusRegister: Number(data.statusRegister?.key),
				guidCountry: String(data.country?.key),
				addresses: formattedAddress,
				contacts: formattedContacts,
				insurerIds: [],
				supplierCompanyRelations: [],
				guidInsurerLog: guidInsurer ?? "",
			};

			supervisorApi.save(request).then((response) => {
				const { result } = response.data;
				toast.success(getTranslateByKey("information.supervisorSavedSuccessfully"));
				setIsEdit(true);
				setSearchParams({ supervisorGuid: result.guid }, { replace: true });
			});
		} catch {}
	}

	return (
		<>
			<Box style={{ marginBottom: "2rem" }}>
				<S.RowInputs>
					<KogAutoCompleteControlled
						label={getTranslateByKey("information.country")}
						control={control}
						nameCustom="country"
						rulesCustom={{ required: true }}
						errorMessageCustom={
							errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
						}
						hasErrorCustom={!!errors.country}
						options={countriesUser.data ?? []}
						isLoading={countriesUser.isLoading}
						onChangeCustom={() => {}}
						disabled={isDisabledCountry()}
						autoHighlightCustom={countriesUser.data?.length === 1}
						setValueCustom={setValue}
						id="id-registerSupervisor-txt-country"
					/>

					<KogInputTextControlledNew
						name="name"
						label={getTranslateByKey("information.name")}
						control={control}
						controllerProps={{ rules: { required: true } }}
						errorMessageCustom={
							errors.name?.type === "required" ? getTranslateByKey("information.nameRequired", false) : ""
						}
						id="id-registerSupervisor-txt-name"
					/>
					<KogInputTextControlledNew
						label={getTranslateByKey("information.ID")}
						helperText={""}
						name="rfc"
						error={!!errors.rfc}
						inputProps={{}}
						controllerProps={{
							rules: {
								required: true,
							},
						}}
						errorMessageCustom={
							errors.rfc?.type === "required" ? getTranslateByKey("information.idRequired", true) : ""
						}
						control={control}
						id="id-registerSupervisor-txt-rfc"
					/>
					<KogAutoCompleteControlled
						label={getTranslateByKey("information.status")}
						nameCustom="statusRegister"
						valueDefaultKey={String(StatusRegisterEnum.Active)}
						control={control}
						setValueCustom={setValue}
						options={statusRegister.data}
						isLoading={statusRegister.isLoading}
						rulesCustom={{ required: true }}
						errorMessageCustom={
							errors.status?.type === "required" ? getTranslateByKey("information.statusRequired", true) : ""
						}
						hasErrorCustom={!!errors.status}
						onChangeCustom={() => {}}
						id="id-registerSupervisor-txt-statusRegister"
					/>
				</S.RowInputs>
			</Box>

			<ContactInfo
				dataContacts={dataContacts}
				setDataContacts={setDataContacts}
				typeCompany=""
				watcherCountry={watcherCountry}
				companyType={CompanyTypeEnum.Supervisor}
				companyName={methods.watch("name")}
			/>

			<AddressInfo
				disableDeliveryAddress
				dataAddress={dataAddress}
				setDataAddress={setDataAddress}
				watcherCountry={watcherCountry}
				typeCompany=""
			/>

			{isEdit && (
				<InsurerCompany
					dataInsurerCompany={dataInsurers}
					guidSupervisor={guidSupervisorParams as string}
					refreshData={() => loadInsurersBySupervisor(guidSupervisorParams ?? "", guidInsurer ?? "")}
				/>
			)}

			<S.ContainerButton>
				<KOGAreaButtons $isRegisterScreen>
					<SecondaryButton type="button" onClick={handleBack} id="id-registerSupervisor-btn-back">
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button
						type="submit"
						id="id-registerSupervisor-btn-save"
						disabled={!hasChanges}
						onClick={methods.handleSubmit(isEdit ? handleSubmitEditSupervisor : handleSubmitRegisterSupervisor)}
					>
						{getTranslateByKey("information.save")}
					</Button>
				</KOGAreaButtons>
			</S.ContainerButton>
			<ModalConfirmationReason />
		</>
	);
}
